// src/Components/ReservationsTable.js
import React, { useEffect, useMemo, useState } from "react";
import styles from "./ReportingTable.module.css";
import { useVarauskalenteritRaportointi } from "../Commoncontext/VarauskalenteritRaportointiContext";
import { useUsers } from "../Commoncontext/UsersContext";

const ReservationsTable = ({ selectedMonths }) => {
  const { raportointiData, setInvoiceStatus } =
    useVarauskalenteritRaportointi();

  const { users = [], user } = useUsers() || {};
  const [userAggregates, setUserAggregates] = useState([]);

  const singleMonth = selectedMonths.length === 1;

  /* -----------------------------------------
     Map: käyttäjäId -> osoite
  ------------------------------------------ */
  const addressByUserId = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      map.set(u.id || u.uid, {
        katu: u.osoite,
      });
    });
    return map;
  }, [users]);

  /* -----------------------------------------
     Aggregointi taulukkoa varten
  ------------------------------------------ */
  useEffect(() => {
    if (!Array.isArray(raportointiData) || raportointiData.length === 0) {
      setUserAggregates([]);
      return;
    }

    const aggregates = {};

    selectedMonths.forEach((monthKey) => {
      const monthData = raportointiData.find(
        (e) => `${e.vuosi}-${e.kuukausi}` === monthKey
      );
      if (!monthData || !Array.isArray(monthData.users)) return;

      monthData.users.forEach((u) => {
        const key = u.käyttäjäid;
        if (!key) return;

        if (!aggregates[key]) {
          const addr = addressByUserId.get(key);
          aggregates[key] = {
            käyttäjäid: key,
            nimi: `${u.etunimi || ""} ${u.sukunimi || ""}`.trim(),
            varaukset: 0,
            hintaYhteensä: 0,
            osoite: addr ? addr.katu : "—",
            months: {}, // { "2025-1": { laskutettu, laskutettuAt, totalPrice } }
          };
        }

        const monthId = `${monthData.vuosi}-${monthData.kuukausi}`;

        const reservations =
          typeof u.reservations === "number" ? u.reservations : 0;
        const price =
          typeof u.totalPrice === "number" && !isNaN(u.totalPrice)
            ? u.totalPrice
            : 0;

        aggregates[key].varaukset += reservations;
        aggregates[key].hintaYhteensä += price;

        // huomioidaan vain kuukaudet joissa käyttäjällä on varauksia
        if (reservations > 0) {
          aggregates[key].months[monthId] = {
            laskutettu: u.laskutettu === true,
            laskutettuAt: u.laskutettuAt || null,
            totalPrice: price,
          };
        }
      });
    });

    setUserAggregates(Object.values(aggregates));
  }, [raportointiData, selectedMonths, addressByUserId]);

  /* -----------------------------------------
     Helper: status multi-month näkymässä
  ------------------------------------------ */
  const getMultiMonthStatus = (row) => {
    const months = Object.values(row.months || []);

    // ei yhtään varauskuukautta
    if (months.length === 0) {
      return { type: "none", label: "Laskuttamatta" };
    }

    const totalPrice = months.reduce(
      (sum, m) => sum + (m.totalPrice || 0),
      0
    );

    // 🔴 varauksia on, mutta ei laskutettavaa
    if (totalPrice === 0) {
      return { type: "zero", label: "Ei laskutettavaa" };
    }

    const invoicedCount = months.filter(
      (m) => m.laskutettu === true
    ).length;

    if (invoicedCount === months.length) {
      return { type: "all", label: "Kaikki laskutettu" };
    }

    if (invoicedCount === 0) {
      return { type: "none", label: "Laskuttamatta" };
    }

    return { type: "partial", label: "Osittain laskutettu" };
  };

  /* -----------------------------------------
     Render
  ------------------------------------------ */
  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Käyttäjä</th>
            <th>Osoite</th>
            <th>Varausten määrä</th>
            <th>Hinta yhteensä</th>
            <th>Laskutettu</th>
          </tr>
        </thead>

        <tbody>
          {userAggregates.length > 0 ? (
            userAggregates.map((row) => {
              const monthData = singleMonth
                ? row.months?.[selectedMonths[0]]
                : null;

              return (
                <tr key={row.käyttäjäid}>
                  <td>{row.nimi}</td>
                  <td>{row.osoite}</td>
                  <td>{row.varaukset} kpl</td>
                  <td>{row.hintaYhteensä.toFixed(2)} €</td>

                  <td>
                    {singleMonth ? (
                      <div className={styles.invoiceStatusWrap}>
                        <input
                          type="checkbox"
                          checked={monthData?.laskutettu === true}
                          onChange={(e) => {
                            const report = raportointiData.find(
                              (r) =>
                                `${r.vuosi}-${r.kuukausi}` ===
                                selectedMonths[0]
                            );
                            if (!report) return;

                            setInvoiceStatus({
                              reportId: report.id,
                              käyttäjäid: row.käyttäjäid,
                              laskutettu: e.target.checked,
                              actorUserId: user?.uid,
                            });
                          }}
                        />

                        {monthData?.laskutettu === true &&
                          monthData?.laskutettuAt && (
                            <span className={styles.invoiceLabel}>
                              Laskutettu{" "}
                              {new Date(
                                monthData.laskutettuAt
                              ).toLocaleDateString("fi-FI")}
                            </span>
                          )}
                      </div>
                    ) : (
                      (() => {
                        const status = getMultiMonthStatus(row);

                        let dotClass = styles.invoiceMissing; // ⚪
                        if (status.type === "all")
                          dotClass = styles.invoiceOk; // 🟢
                        else if (status.type === "partial")
                          dotClass = styles.invoicePartial; // 🟡
                        else if (status.type === "zero")
                          dotClass = styles.invoiceZero; // 🔴

                        return (
                          <div className={styles.invoiceStatusWrap}>
                            <span
                              className={`${styles.invoiceDot} ${dotClass}`}
                            />
                            <span className={styles.invoiceLabel}>
                              {status.label}
                            </span>
                          </div>
                        );
                      })()
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="5" className={styles.emptyRow}>
                Ei varauksia valitulta ajanjaksolta
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ReservationsTable;
